<script lang="ts">
  import { HEAT, heatLevel } from '@ui/heat';
  import ChartTooltip from '@ui/ChartTooltip.svelte';
  import ActivityBreakdown from '@ui/ActivityBreakdown.svelte';

  /** One weekly bucket. `total` is posts + comments — what the heat ramp shades — and
   *  the breakdown rides alongside for the hover. The three parts are optional because
   *  a caller on an older payload has only `week`/`total`. */
  export type WeekBucket = {
    week: string;
    total: number;
    posts?: number;
    comments?: number;
    likes?: number;
  };

  // The weekly contribution strip — twelve months, one cell per week, shaded on the
  // shared intensity ramp.
  //
  // Extracted from the members table so the pipeline card renders the SAME component
  // off the SAME `weekly` series (`api.v1.members`' shape, mirrored by the board's
  // `_weekly`). Two hand-rolled copies of a heat ramp is how the same member ends up
  // looking busier on one screen than another.
  //
  // Deliberately unlabelled and axis-free: at this size it is a texture, not a chart.
  // It answers "is this person warming up or going cold" at a glance, and the hover
  // carries the numbers for anyone who wants them.
  //
  // THE HOVER IS THE SAME ONE `<Heatmap>` USES — the shared `<ChartTooltip>`, anchored
  // to the cell's own rect and rendered `fixed` so it escapes the card's overflow, plus
  // the same `hover:z-10 hover:scale-*` lift on the cell under the cursor. A week here
  // is ~3px wide inside a 17rem column, so the lift is bigger than the heatmap's 125%:
  // at that size a quarter more is not a change anyone can see, and the point of the
  // enlargement is to confirm WHICH cell the numbers belong to.
  let {
    weeks,
    max,
    height = 'h-4',
    title = 'Posts + comments per week, last 12 months (UTC)',
  }: {
    weeks: WeekBucket[];
    max: number;
    /** Tailwind height class — the card wants a shorter strip than the table row. */
    height?: string;
    title?: string;
  } = $props();

  type Hover = { w: WeekBucket; x: number; y: number };
  let hover = $state<Hover | null>(null);

  function enter(e: MouseEvent, w: WeekBucket): void {
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    hover = { w, x: r.left + r.width / 2, y: r.top };
  }

  /** `YYYY-MM-DD` bucket label → a date a reader recognises. Parsed as UTC parts rather
   *  than `new Date(str)` so the bucket cannot shift a day west of Greenwich. */
  function weekLabel(iso: string): string {
    const [y, m, d] = iso.split('-').map(Number);
    if (!y || !m || !d) return iso;
    return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC',
    });
  }
</script>

{#if weeks.length}
  <div class="flex items-stretch gap-[2px] {height}" {title}>
    {#each weeks as w (w.week)}
      <!-- No per-cell `title`: the native tooltip would race the rich one and win,
           which is the same reason `<Heatmap>` drops it in its detailed mode. -->
      <!-- svelte-ignore a11y_no_static_element_interactions -->
      <div
        class={'min-w-0 flex-1 cursor-pointer rounded-[2px] transition-transform hover:z-10 hover:scale-y-150 hover:scale-x-[2.5] ' +
          HEAT[heatLevel(w.total, max)]}
        onmouseenter={(e) => enter(e, w)}
        onmouseleave={() => (hover = null)}
      ></div>
    {/each}
  </div>
{:else}
  <span class="text-base-content/68">—</span>
{/if}

{#if hover}
  {@const h = hover}
  <ChartTooltip x={h.x} y={h.y} class="w-44 px-3 py-2">
    <div class="font-mono text-[0.7rem] font-semibold">week of {weekLabel(h.w.week)}</div>
    <ActivityBreakdown posts={h.w.posts} comments={h.w.comments} likes={h.w.likes} />
  </ChartTooltip>
{/if}
